﻿import React, { useState, useEffect, useContext, useMemo } from "react";
import { useParams, useHistory, useLocation } from "react-router-dom";

import { toast } from "react-toastify";
import openSocket from "../../services/socket-io";
import clsx from "clsx";

import { Paper, makeStyles } from "@material-ui/core";

import ContactDrawer from "../ContactDrawer";
import MessageInput from "../MessageInput/";
import TicketHeader from "../TicketHeader";
import TicketInfo from "../TicketInfo";
import TicketActionButtons from "../TicketActionButtons";
import MessagesList from "../MessagesList";
import api from "../../services/api";
import { ReplyMessageProvider } from "../../context/ReplyingMessage/ReplyingMessageContext";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";

const drawerWidth = 320;

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    height: "100%",
    position: "relative",
    overflow: "hidden",
  },

  ticketInfo: {
    flex: 1,
    minWidth: 0,
    maxWidth: "none",
    [theme.breakpoints.down("sm")]: {
      maxWidth: "100%",
      flexBasis: "100%",
    },
  },
  ticketActionButtons: {
    flex: "none",
    marginLeft: "auto",
    display: "flex",
    alignItems: "center",
    [theme.breakpoints.down("sm")]: {
      maxWidth: "100%",
      flexBasis: "100%",
      marginBottom: 4,
      marginLeft: 0,
      justifyContent: "flex-end"
    },
  },

  mainWrapper: {
    flex: 1,
    height: "100%",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    borderTopLeftRadius: 0,
    borderBottomLeftRadius: 0,
    borderLeft: "0",
    marginRight: -drawerWidth,
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
  },

  mainWrapperShift: {
    borderTopRightRadius: 0,
    borderBottomRightRadius: 0,
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.easeOut,
    duration: theme.transitions.duration.enteringScreen,
    }),
    marginRight: 0,
  },

  wabaWindowBar: {
    width: "100%",
    padding: "8px 14px",
    fontSize: "0.82rem",
    fontWeight: 600,
    borderTop: "1px solid rgba(0, 0, 0, 0.08)",
    borderBottom: "1px solid rgba(0, 0, 0, 0.08)",
  },
  wabaWindowBarActive: {
    backgroundColor: "#E9F7FF",
    color: "#0B4F71",
  },
  wabaWindowBarExpired: {
    backgroundColor: "#FFF1F0",
    color: "#8A1C1C",
  },
}));

const Ticket = () => {
  const { ticketId } = useParams();
  const history = useHistory();
  const location = useLocation();
  const classes = useStyles();

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [contact, setContact] = useState({});
  const [ticket, setTicket] = useState({});
  const { user } = useContext(AuthContext);
  const internalTicketId = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get("internalTicketId");
  }, [location.search]);

  const fetchTicket = async () => {
    try {
      const { data } = await api.get("/tickets/" + ticketId, {
        params: internalTicketId ? { internalTicketId } : undefined
      });

      setContact(data.contact);
      setTicket(data);
      setLoading(false);
    } catch (err) {
      setLoading(false);
      const statusCode = Number(err?.response?.status || 0);
      const backendError = String(err?.response?.data?.error || "");
      if (statusCode === 404 || backendError === "ERR_NO_TICKET_FOUND") {
        history.push("/tickets");
        return;
      }
      toastError(err);
    }
  };

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      fetchTicket();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [ticketId, history, internalTicketId]);

  useEffect(() => {
    const socket = openSocket();

    socket.on("connect", () =>
      socket.emit(
        "joinChatBox",
        internalTicketId ? { ticketId, internalTicketId } : ticketId
      )
    );

    socket.on("ticket", (data) => {
      if (data.action === "update") {
        setTicket((prevState) => ({
          ...prevState,
          ...data.ticket,
        }));
        fetchTicket();
      }

      if (data.action === "delete") {
        if (data.reason === "ticket-deleted") {
          toast.success("Ticket excluido com sucesso.");
        }
        history.push("/tickets");
      }
    });

    socket.on("contact", (data) => {
      if (data.action === "update") {
        setContact((prevState) => {
          if (prevState.id === data.contact?.id) {
            return { ...prevState, ...data.contact };
          }
          return prevState;
        });
      }
    });

    socket.on("wabaCall", data => {
      if (Number(data?.ticketId) === Number(ticketId)) {
        fetchTicket();
      }
    });

    socket.on("wabaCallControl", data => {
      if (Number(data?.ticketId) === Number(ticketId)) {
        fetchTicket();
      }
    });

    return () => {
      socket.disconnect();
    };
  }, [ticketId, history, internalTicketId]);

  const handleDrawerOpen = () => {
    setDrawerOpen(true);
  };

  const handleDrawerClose = () => {
    setDrawerOpen(false);
  };

  const canWriteToTicket =
    ticket.status === "open" &&
    (!ticket.userId || Number(ticket.userId) === Number(user?.id));
  const canCommentOnTicket =
    ticket.status === "open" &&
    (user?.profile === "admin" ||
      user?.profile === "superadmin" ||
      (ticket.userId && Number(ticket.userId) === Number(user?.id)));
  const canEditSubject =
    ticket.status === "open" &&
    (user?.profile === "admin" ||
      !ticket.userId ||
      Number(ticket.userId) === Number(user?.id));

  const isReadOnlyInternalView =
    Boolean(internalTicketId) &&
    user?.profile !== "admin" &&
    !canWriteToTicket;

  const isWabaTicket =
    String(ticket?.whatsapp?.connectionType || "").toLowerCase() === "waba" &&
    Boolean(ticket?.whatsapp?.wabaEnabled);
  const isPlatformInitiatedWabaConversation = Boolean(
    ticket?.wabaConversationInitiatedByPlatform
  );
  const wabaTemplateRequired = Boolean(ticket?.wabaTemplateRequired);
  const wabaWindowExpiresAt = ticket?.wabaWindowExpiresAt
    ? new Date(ticket.wabaWindowExpiresAt)
    : null;
  const formatDateTime = value => {
    if (!value || Number.isNaN(value.getTime())) return "-";
    return value.toLocaleString("pt-BR");
  };
  return (
    <div className={classes.root} id="drawer-container">
      <Paper
        variant="outlined"
        elevation={0}
        className={clsx(classes.mainWrapper, {
          [classes.mainWrapperShift]: drawerOpen,
        })}
      >
        <TicketHeader loading={loading}>
          <div className={classes.ticketInfo}>
            <TicketInfo
              contact={contact}
              ticket={ticket}
              onClick={handleDrawerOpen}
              canEditSubject={canEditSubject}
              subjectRequired={Boolean(ticket.subjectRequired)}
            />
          </div>
          <div className={classes.ticketActionButtons}>
            <TicketActionButtons
              ticket={ticket}
              onRefreshTicket={fetchTicket}
              readOnly={isReadOnlyInternalView}
            />
          </div>
        </TicketHeader>
        {isWabaTicket && isPlatformInitiatedWabaConversation && (
          <div
            className={clsx(classes.wabaWindowBar, {
              [classes.wabaWindowBarActive]: !wabaTemplateRequired,
              [classes.wabaWindowBarExpired]: wabaTemplateRequired,
            })}
          >
            {wabaTemplateRequired
              ? "Janela de 24h expirada. Para iniciar conversa, envie template WABA."
              : `Janela de 24h ativa ate ${formatDateTime(wabaWindowExpiresAt)}.`}
          </div>
        )}
        <ReplyMessageProvider>
          <MessagesList
            ticketId={ticketId}
            isGroup={ticket.isGroup}
            internalTicketId={internalTicketId}
            canInteract={canWriteToTicket}
          ></MessagesList>
          <MessageInput
            ticketStatus={ticket.status}
            canWrite={canWriteToTicket}
            canComment={canCommentOnTicket}
            ticketSubject={ticket.subject}
            subjectRequired={Boolean(ticket.subjectRequired)}
            isGroup={Boolean(ticket.isGroup)}
            ticketWhatsapp={ticket.whatsapp}
          />
        </ReplyMessageProvider>
      </Paper>
      <ContactDrawer
        open={drawerOpen}
        handleDrawerClose={handleDrawerClose}
        contact={contact}
        loading={loading}
        currentTicketId={ticket?.id}
        ticketHistory={ticket?.ticketHistory || []}
      />
    </div>
  );
};

export default Ticket;


